import type { CardSlot } from '@kenocyber/shared';

/** O número de cada espaço, para a ponte. O fio conta bytes. */
const PVP_CARD_NUMBER: Readonly<Record<CardSlot, 1 | 2 | 3>> = {
  card_slot_1: 1,
  card_slot_2: 2,
  card_slot_3: 3,
};

import { PVP_RULES_VERSION } from '@kenocyber/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptPvpChallenge,
  createPvpChallenge,
  findActivePvpMatch,
  leavePvpDuel,
  mintPvpSocketTicket,
  pvpSocketUrl,
} from '../api/pvp';
import {
  createPvpTeamLobby,
  joinPvpTeamLobby,
  launchPvpTeamLobby,
  leavePvpTeamLobby,
  mintPvpTeamSocketTicket,
  pvpTeamSocketUrl,
  readPvpTeamLobby,
} from '../api/pvp-team';
import type { GameBridge } from '../game/bridge';
import type { PauseCoordinator } from '../game/pause-coordinator';
import { browserPvpSocket, PvpConnection } from './connection';
import type { PvpControllerDeps } from './controller';
import { PVP_IDLE_INTENT } from './controls';
import { PvpModeChoice } from './PvpModeChoice';
import { PvpPanel } from './PvpPanel';
import { PvpTeamPanel } from './PvpTeamPanel';
import { type PvpPresentation, pvpPresentation } from './selectors';
import { PvpTeamConnection } from './team-connection';
import type { PvpTeamControllerDeps } from './team-controller';
import { pvpTeamPresentation } from './team-selectors';
import { usePvpController } from './usePvpController';
import { useTeamPvpController } from './useTeamPvpController';

/**
 * Where the duel is plugged into the game.
 *
 * ## Why this exists instead of more `App.tsx`
 *
 * The App already owns the account, the hunt, the bridge and the overlays, and
 * every one of those is a good reason for the duel to live NEAR it - and none
 * is a reason for the duel to live INSIDE it. What the duel needs from the App
 * is four values; what it gives back is one flag object and one panel. Wiring
 * that here keeps the twelve dependencies, the socket factory and the pause
 * lease out of a component that is already long.
 *
 * ## It owns the controller and nothing else
 *
 * No reducer, no reconnection, no operation id, no ticket. Those are
 * `createPvpController`, which is proven without React. This component builds
 * the dependency bag, hands it to the hook, and renders.
 *
 * ## The ticket never lands anywhere it could be read
 *
 * `mintPvpSocketTicket` answers into `PvpConnection`, which puts it straight
 * into a socket URL and forgets it. It is never React state, never a prop,
 * never an attribute and never logged - and the panel below is not given it to
 * begin with.
 */

export interface PvpFeatureProps {
  readonly bridge: GameBridge;
  readonly pause: PauseCoordinator;
  /**
   * The hunt's own authoritative bootstrap, through the path it already uses
   * for a conflict. Not a second implementation, and not a stored snapshot.
   */
  readonly rebootstrapHunt: () => Promise<void>;
  /**
   * The shell, rendered with what the duel derives.
   *
   * A render prop rather than context: the App needs exactly two things - the
   * flags and a way to open - and passing them down explicitly keeps the duel
   * from becoming an ambient dependency of every component below it.
   */
  readonly children: (view: {
    readonly presentation: PvpPresentation;
    readonly open: () => void;
  }) => React.ReactNode;
}

/**
 * The one thing this client stores, and what it deliberately is not.
 *
 * A match id, in `sessionStorage`, meaning "this tab left that duel on
 * purpose". It is not a credential, not a seat, not a resume hint and not a
 * source of truth: the only thing it can do is REFUSE a rejoin the server
 * would otherwise have allowed. Forging it grants nothing - the same effect
 * is one click on SAIR - and losing it costs one unwanted rejoin.
 *
 * Per tab, because the intention was expressed in a tab. Signing in as
 * somebody else cannot be affected by it either way: the id is compared
 * against a match the SERVER named for the current session, so a stale entry
 * from another account simply never matches.
 *
 * Storage may be unavailable - a locked-down browser, private mode in some
 * engines, a quota that is full. Every path is wrapped: without it the
 * player is rejoined after pressing SAIR and reloading inside the grace,
 * which is a wrong screen for a few seconds rather than anything unsafe.
 */
const LEFT_DUEL_KEY = 'kenocyber:pvp:left';

function rememberLeftDuel(matchId: string): void {
  try {
    window.sessionStorage.setItem(LEFT_DUEL_KEY, matchId);
  } catch {
    // No storage. See above: this degrades, it does not break.
  }
}

function leftThisDuel(matchId: string): boolean {
  try {
    return window.sessionStorage.getItem(LEFT_DUEL_KEY) === matchId;
  } catch {
    return false;
  }
}

export function PvpFeature({ bridge, pause, rebootstrapHunt, children }: PvpFeatureProps) {
  /*
   * Opening the arena's target mode, as a bridge MESSAGE.
   *
   * React owns the button; Phaser owns the cursor and the ring. Neither reaches
   * into the other, which is the same rule every other thing the HUD asks the
   * scene for follows. Arming grants nothing, so there is no state to keep here
   * - what eventually crosses the socket is one point, once.
   */
  const armTeleport = useCallback(() => {
    bridge.sendToGame('pvp:arm-teleport', {});
  }, [bridge]);
  /*
   * A ordem de DETONAR, como uma MENSAGEM da ponte (gate V28G.N).
   *
   * React é dono do botão; Phaser é dono do frame de comando que o leva. Nenhum
   * dos dois chega ao outro, que é a mesma regra que a afordância do teleporte
   * segue - e nada aqui guarda estado, porque a ordem é o próprio clique.
   */
  const detonateBleed = useCallback(() => {
    bridge.sendToGame('pvp:detonate-bleed', {});
  }, [bridge]);
  /*
   * E a ORDEM de ligar uma carta, pela mesma ponte (gate V28H.J).
   *
   * A identidade da ordem nasce aqui, no clique ou na tecla, e é ela que faz uma
   * repetição ser lida do outro lado em vez de ligar a carta outra vez.
   */
  /**
   * **Há um Leviatã encostado?** (gate V28H.X).
   *
   * A CENA responde - é ela que tem as posições desenhadas e o raio de entrada -
   * e publica só quando a resposta muda. Este estado existe para o botão saber
   * se se desenha, e para mais nada.
   */
  const [canBoard, setCanBoard] = useState(false);
  useEffect(
    () => bridge.onUiMessage('pvp:board-offer', ({ available }) => setCanBoard(available)),
    [bridge],
  );

  /**
   * **A ordem de PILOTAR** (gate V28H.X).
   *
   * A mesma forma da ordem de uma carta: uma identidade nova por carregar, e
   * nada mais. Ela não diz em que estrutura se entra, porque não há onde o
   * dizer - a sala escolhe a que o casco está a tocar.
   */
  const board = useCallback(() => {
    bridge.sendToGame('pvp:board-summon', { commandId: crypto.randomUUID() });
  }, [bridge]);

  const activateCard = useCallback(
    (slot: CardSlot) => {
      bridge.sendToGame('pvp:activate-card', {
        slot: PVP_CARD_NUMBER[slot],
        commandId: crypto.randomUUID(),
      });
    },
    [bridge],
  );
  /*
   * **E a ordem de SOLTAR a carga** (gate V28H.N).
   *
   * A Lança Solar é carregada enquanto a tecla ou o botão permanece premido, e
   * o instante em que o dedo sai é uma decisão do jogador. Sob autoridade da
   * sala, uma decisão que não chega lá não existe - por isso ela viaja, com a
   * sua própria identidade, exactamente como a de ligar.
   */
  const releaseCard = useCallback(
    (slot: CardSlot) => {
      bridge.sendToGame('pvp:release-card', {
        slot: PVP_CARD_NUMBER[slot],
        commandId: crypto.randomUUID(),
      });
    },
    [bridge],
  );
  /*
   * The dependency bag.
   *
   * Rebuilt whenever the bridge, the coordinator or the ACCOUNT changes - and
   * the hook holds it behind a ref, so a new object here does not rebuild the
   * controller. What does rebuild it is the `key` on this component, which the
   * App sets from the same account.
   */
  const deps = useMemo<PvpControllerDeps>(
    () => ({
      createChallenge: async () => {
        const made = await createPvpChallenge();
        return { matchId: made.matchId, code: made.code, seat: made.seat };
      },
      leftMatches: {
        remember: (matchId) => rememberLeftDuel(matchId),
        was: (matchId) => leftThisDuel(matchId),
      },
      findActiveMatch: async () => {
        const active = await findActivePvpMatch();
        return active ? { matchId: active.matchId, seat: active.seat, state: active.state } : null;
      },
      acceptChallenge: async (code) => {
        const joined = await acceptPvpChallenge(code);
        return { matchId: joined.matchId, seat: joined.seat };
      },
      openConnection: () =>
        new PvpConnection({
          // One use, thirty seconds, and a fresh one for every attempt. It goes
          // from here into a URL and nowhere else.
          mintTicket: async (matchId) => ({ ticket: (await mintPvpSocketTicket(matchId)).ticket }),
          socketUrl: pvpSocketUrl,
          openSocket: browserPvpSocket,
          now: () => Date.now(),
          schedule: (run, ms) => {
            const timer = window.setTimeout(run, ms);
            return () => window.clearTimeout(timer);
          },
          // What this bundle was built against. A server running other numbers
          // is refused rather than drawn.
          rulesVersion: PVP_RULES_VERSION,
        }),

      /* -- presentation -- */

      acquirePause: () => pause.acquire('pvp'),
      setHuntVisible: (visible) => {
        bridge.sendToGame('hunt:visible', { visible });
      },
      /*
       * Deliberately nothing, and that is a finding rather than an omission.
       *
       * The hunt reads NO keyboard and NO pointer: its ship is fixed in the
       * bottom-centre region and fires automatically, and a search of
       * `HuntScene` for `input.keyboard`, `createCursorKeys`, `addKey`,
       * `pointermove` and `pointerdown` returns nothing at all. There is no
       * held key, no held button and no persistent intent for a duel to clear.
       *
       * The controller still calls this, because "clear whatever the other mode
       * was holding" is the right step in the transition and the day the hunt
       * grows a control is the day this stops being empty. What it must NOT do
       * is what it did in the first draft: publish `ui:set-paused`. Pausing is
       * the coordinator's, and a second publisher of that message is exactly
       * the bug the coordinator was built to remove.
       */
      clearHuntIntent: () => {},
      openArena: (seat) => {
        bridge.sendToGame('pvp:open', { seat, ruleset: 'duel_1v1' });
      },
      publishAuthoritative: (state) => {
        bridge.sendToGame('pvp:authoritative', { state });
      },
      publishProcs: (cuts) => {
        bridge.sendToGame('pvp:proc', { cuts });
      },
      publishBlasts: (blasts) => {
        bridge.sendToGame('pvp:blast', { blasts });
      },
      publishBleedBursts: (bursts) => {
        bridge.sendToGame('pvp:bleed', { bursts });
      },
      publishReflects: (reflects) => {
        bridge.sendToGame('pvp:reflect', { reflects });
      },
      intentSent: (seq, intent) => {
        bridge.sendToGame('pvp:intent-sent', { seq, intent });
      },
      closeArena: () => {
        bridge.sendToGame('pvp:close', {});
        // Nothing is held after the arena goes: a key still down would be sent
        // the instant another duel opened.
        bridge.sendToUi('pvp:intent', { intent: PVP_IDLE_INTENT });
      },

      /* -- the way back -- */

      bootstrapHunt: () => rebootstrapHunt(),
    }),
    [bridge, pause, rebootstrapHunt],
  );

  /**
   * The team lobby's own dependency bag.
   *
   * Deliberately a second object rather than a widened first one: not one field
   * is shared with the duel's, because not one route is. What the two DO share
   * is the presentation half - the pause lease, the visibility message, the
   * arena and the bootstrap - and those are the same functions here, so the two
   * modes cannot end up with two different ideas of how a hunt is put back.
   */
  const teamDeps = useMemo<PvpTeamControllerDeps>(
    () => ({
      createLobby: () => createPvpTeamLobby(),
      joinLobby: (code) => joinPvpTeamLobby(code),
      readLobby: () => readPvpTeamLobby(),
      launchLobby: () => launchPvpTeamLobby(),
      leaveLobby: () => leavePvpTeamLobby(),
      openConnection: () =>
        new PvpTeamConnection({
          // One use, thirty seconds, and a fresh one for every attempt. It goes
          // from here into a URL and nowhere else - never React state, never a
          // prop, never an attribute, never storage and never a log.
          mintTicket: async () => ({ ticket: (await mintPvpTeamSocketTicket()).ticket }),
          socketUrl: pvpTeamSocketUrl,
          openSocket: browserPvpSocket,
          now: () => Date.now(),
          schedule: (run, ms) => {
            const timer = window.setTimeout(run, ms);
            return () => window.clearTimeout(timer);
          },
          rulesVersion: PVP_RULES_VERSION,
        }),
      /*
       * How often the roster is re-read while the lobby is on screen.
       *
       * Six chairs fill over minutes and there is no socket until the roster is
       * frozen, so this is the only way anybody sees anybody arrive. Two seconds
       * is well under what a person waiting notices and well over what six
       * clients can turn into load: one read of six rows each, scoped to their
       * own chair, and it stops the instant the lobby screen does.
       */
      pollIntervalMs: 2_000,
      schedule: (run, ms) => {
        const timer = window.setTimeout(run, ms);
        return () => window.clearTimeout(timer);
      },

      /* -- presentation, identical to the duel's -- */

      acquirePause: () => pause.acquire('pvp'),
      setHuntVisible: (visible) => {
        bridge.sendToGame('hunt:visible', { visible });
      },
      clearHuntIntent: () => {},
      openArena: (participantId) => {
        /*
         * The SAME message and the same scene as a duel.
         *
         * `pvp:open` has carried a participant id since the renderer stopped
         * assuming there were two ships; in a 1x1 that id is the seat. So a 3x3
         * needs no second message, no second scene, no second canvas and no
         * second WebGL context - it passes `a2` where the duel passes `a`.
         *
         * The RULESET is the one thing the two openings say differently, and it
         * decides one thing: which VFX profile a skin compiles with. Six ships
         * cost more than two, so `pvp3x3` is a smaller signature than `pvp1x1` -
         * a difference the descriptors already author and this message is what
         * selects.
         */
        bridge.sendToGame('pvp:open', { seat: participantId, ruleset: 'team_3v3' });
      },
      publishAuthoritative: (state) => {
        // The WORLD, which is a `PvpClientState` exactly as the duel produces
        // one. The team-only parts of the client state - the roster, the chair,
        // the winning team - are rules rather than pictures and stay in React.
        bridge.sendToGame('pvp:authoritative', { state: state.world });
      },
      publishProcs: (cuts) => {
        bridge.sendToGame('pvp:proc', { cuts });
      },
      publishBlasts: (blasts) => {
        bridge.sendToGame('pvp:blast', { blasts });
      },
      publishBleedBursts: (bursts) => {
        bridge.sendToGame('pvp:bleed', { bursts });
      },
      publishReflects: (reflects) => {
        bridge.sendToGame('pvp:reflect', { reflects });
      },
      intentSent: (seq, intent) => {
        bridge.sendToGame('pvp:intent-sent', { seq, intent });
      },
      closeArena: () => {
        bridge.sendToGame('pvp:close', {});
        bridge.sendToUi('pvp:intent', { intent: PVP_IDLE_INTENT });
      },

      bootstrapHunt: () => rebootstrapHunt(),
    }),
    [bridge, pause, rebootstrapHunt],
  );

  const pvp = usePvpController(deps);
  const team = useTeamPvpController(teamDeps);

  /*
   * Giving up the duel the server says this account is already in.
   *
   * Deliberately NOT routed through the controller's machine: there is no
   * local state to unwind. The refusal happened before anything was opened -
   * no socket, no seat here, no arena - so what has to change is on the
   * server, and the only thing left to do afterwards is clear the sentence
   * that is now stale.
   *
   * A failure leaves the message where it is. The player has lost nothing and
   * can press again; swallowing it and pretending the duel was cancelled
   * would send them back to `CRIAR DESAFIO` to be refused a second time.
   */
  const cancelStuckDuel = useCallback(() => {
    void leavePvpDuel()
      .then(() => {
        pvp.codeEdited();
      })
      .catch(() => undefined);
  }, [pvp]);

  /*
   * Which mode the player asked for, before either lobby exists.
   *
   * Local to this component and deliberately not a state in either machine: it
   * is not a duel and not a team match, it is a question, and neither controller
   * should have a state for "nobody has decided yet". It is cleared the moment
   * one of them opens, so there is never a frame with the question and a lobby
   * on screen together.
   */
  const [choosingMode, setChoosingMode] = useState(false);
  const duelOpen = pvp.state.kind !== 'closed';
  const teamOpen = team.state.kind !== 'closed';

  /*
   * One presentation, from whichever mode is actually on screen.
   *
   * The App reads a single object and does not know which mode produced it,
   * which is what keeps the shell from growing a branch per mode. The order
   * matters and is not arbitrary: a mode that has a lobby wins over the question
   * that opened it, and the two lobbies are mutually exclusive because the
   * server refuses an account a second live match.
   */
  const presentation: PvpPresentation = teamOpen
    ? pvpTeamPresentation(team.state)
    : duelOpen
      ? pvpPresentation(pvp.state)
      : /*
         * The question itself, described with the duel's own `choosing` flags:
         * a panel is up, the hunt keeps running behind it, the shell's PVP
         * button is not pressable, and there is a way out.
         */
        pvpPresentation(choosingMode ? { kind: 'choosing', lastError: null } : { kind: 'closed' });

  const openPvp = useCallback(() => {
    // Only from nothing at all. A click while a lobby is up must not replace it
    // with a question.
    setChoosingMode(true);
  }, []);

  /*
   * The only thing that carries the player's hands to the room.
   *
   * `PvpScene` reads WASD, the pointer and the trigger, coalesces them to the
   * protocol's cadence and publishes ONE intent per step. Nothing listened to
   * that message until two real browsers tried to duel and found two ships
   * that could not move: the scene was publishing into an empty room.
   *
   * The handler goes through a ref so the subscription is installed once and
   * never re-registers. A listener rebuilt on every render would miss the
   * intents that arrive between renders, which at twenty a second is most of
   * them.
   */
  const sendIntent = useRef(pvp.sendIntent);
  sendIntent.current = pvp.sendIntent;
  /*
   * And the same hands, on the same bus, for the other mode.
   *
   * Both are offered every intent and each drops it unless ITS OWN match is
   * running - which is a guard both controllers already have, in the state
   * machine where the rest of their rules live. Choosing between them here would
   * be a third place that has to know which mode is live.
   */
  const sendTeamIntent = useRef(team.sendIntent);
  sendTeamIntent.current = team.sendIntent;
  useEffect(
    () =>
      bridge.onUiMessage('pvp:intent', ({ intent }) => {
        sendIntent.current(intent);
        sendTeamIntent.current(intent);
      }),
    [bridge],
  );

  /*
   * On mount, ask the server whether this account is already in a duel.
   *
   * A reload loses everything the tab knew and the room keeps the seat for
   * twelve seconds regardless, so the only thing missing was somebody coming
   * back to claim it. Measured before this existed: the page came back, made no
   * PvP request at all, and the grace expired into an abandonment the opponent
   * was told about 12.1 seconds after the reload.
   *
   * Once, and only for this mounted feature - the App gives this component a
   *  from the account, so signing in as somebody else remounts it and asks
   * again as them.  refuses unless the panel is closed, which is where
   * a freshly loaded page is and where a player who has already started typing
   * a code is not.
   */
  const resume = useRef(pvp.resume);
  resume.current = pvp.resume;
  /*
   * The team lobby asks the same question of its own route.
   *
   * Two reads rather than one because they are two tables and two answers: a
   * duel is a seat in `pvp_matches` and a team chair is a row in a lobby that
   * may not even have frozen yet. Both refuse unless their panel is closed, and
   * an account cannot be in both - the server refuses a second live match - so
   * at most one of them ever opens anything.
   */
  const resumeTeam = useRef(team.resume);
  resumeTeam.current = team.resume;
  useEffect(
    () =>
      bridge.onUiMessage('pvp:scene-ready', () => {
        resume.current();
        resumeTeam.current();
      }),
    [bridge],
  );

  /*
   * A mode that opened its own lobby answers the question that opened it.
   *
   * Cleared in an effect rather than inside the click handler because a resume
   * can open one of them without anybody clicking anything - a reload into a
   * running match must not leave the question on screen behind the arena.
   */
  useEffect(() => {
    if (duelOpen || teamOpen) {
      setChoosingMode(false);
    }
  }, [duelOpen, teamOpen]);

  return (
    <>
      {children({ presentation, open: openPvp })}
      {teamOpen ? (
        <PvpTeamPanel
          state={team.state}
          presentation={presentation}
          onCreate={team.createLobby}
          onJoin={team.joinLobby}
          onCodeEdited={team.codeEdited}
          onLaunch={team.launch}
          onReady={team.markReady}
          onLeave={team.leave}
          onRetryRecovery={team.retryHuntRecovery}
          onArmTeleport={armTeleport}
          onDetonateBleed={detonateBleed}
          onActivateCard={activateCard}
          onReleaseCard={releaseCard}
        />
      ) : duelOpen ? (
        <PvpPanel
          state={pvp.state}
          presentation={presentation}
          onCreate={pvp.createChallenge}
          onJoin={pvp.joinChallenge}
          onCodeEdited={pvp.codeEdited}
          onReady={pvp.markReady}
          onLeave={pvp.leave}
          onRetryRecovery={pvp.retryHuntRecovery}
          onCancelStuck={cancelStuckDuel}
          onArmTeleport={armTeleport}
          onDetonateBleed={detonateBleed}
          onActivateCard={activateCard}
          onReleaseCard={releaseCard}
          canBoard={canBoard}
          onBoard={board}
        />
      ) : choosingMode ? (
        <PvpModeChoice
          onChooseDuel={() => {
            setChoosingMode(false);
            pvp.open();
          }}
          onChooseTeam={() => {
            setChoosingMode(false);
            team.open();
          }}
          onClose={() => setChoosingMode(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The button that opens a duel.
 *
 * Separate from the panel because it lives in the shell's tab row, which the
 * App builds - and because the two have nothing to say to each other beyond
 * "open".
 */
export function PvpOpenButton({
  enabled,
  onOpen,
}: {
  readonly enabled: boolean;
  readonly onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="arena-tab pvp-open-button"
      data-testid="pvp-open"
      // Not `aria-expanded`: the duel is not an overlay over the arena, it
      // REPLACES it, and the button is gone while it is open.
      disabled={!enabled}
      onClick={onOpen}
    >
      PVP
    </button>
  );
}
